import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DollarSign, Users, CalendarCheck, Clock, LogOut, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ToursManager from "@/components/ToursManager";
import { toast } from "sonner";

const STATUS_STYLES = {
  confirmed: "text-green-700 bg-green-50 border-green-200",
  pending: "text-amber-700 bg-amber-50 border-amber-200",
  cancelled: "text-red-700 bg-red-50 border-red-200",
  completed: "text-blue-700 bg-blue-50 border-blue-200",
};

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (checked && (!user || user.role !== "admin")) {
      navigate("/admin/login");
    }
  }, [checked, user, navigate]);

  const load = async () => {
    try {
      const [b, s] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (checked && user && user.role === "admin") load();
  }, [checked, user]);

  const changeStatus = async (booking_id, status) => {
    try {
      await api.patch(`/admin/bookings/${booking_id}/status`, { status });
      toast.success(`Booking marked ${status}`);
      load();
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (!checked || (user && user.role !== "admin")) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-signal" /></div>;
  }

  const statCards = stats ? [
    { icon: <CalendarCheck />, label: "Confirmed Bookings", value: stats.total_bookings },
    { icon: <DollarSign />, label: "Revenue (CAD)", value: `$${stats.total_revenue.toFixed(2)}` },
    { icon: <Users />, label: "Total Riders", value: stats.total_riders },
    { icon: <Clock />, label: "Pending", value: stats.pending },
  ] : [];

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      <header className="bg-void text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div>
            <span className="font-display font-black text-xl tracking-tighter">YEG SCOOT<span className="text-signal">.</span></span>
            <span className="ml-3 font-mono text-xs uppercase tracking-widest text-white/50">Admin</span>
          </div>
          <button onClick={handleLogout} data-testid="logout-button"
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/70 hover:text-signal transition-colors">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-10">Dashboard</h1>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="rounded-none bg-secondary mb-10">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-void data-[state=active]:text-white">Bookings</TabsTrigger>
            <TabsTrigger value="tours" data-testid="tab-tours" className="rounded-none font-mono text-xs uppercase tracking-widest data-[state=active]:bg-void data-[state=active]:text-white">Tours</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-border mb-12" data-testid="stats-grid">
          {statCards.map((c) => (
            <div key={c.label} className="border-b border-r border-border p-6">
              <div className="text-signal mb-4">{c.icon}</div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{c.label}</p>
              <p className="font-display font-black text-3xl tracking-tighter mt-1">{c.value}</p>
            </div>
          ))}
        </div>

        {/* Bookings table */}
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="mx-auto animate-spin text-signal" /></div>
        ) : bookings.length === 0 ? (
          <div className="py-20 text-center font-mono text-muted-foreground border border-border" data-testid="no-bookings">
            No bookings yet.
          </div>
        ) : (
          <div className="border border-border overflow-x-auto" data-testid="bookings-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary border-b border-border font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="text-left px-5 py-4">Customer</th>
                  <th className="text-left px-5 py-4">Tour</th>
                  <th className="text-left px-5 py-4">Date / Time</th>
                  <th className="text-left px-5 py-4">Riders</th>
                  <th className="text-left px-5 py-4">Amount</th>
                  <th className="text-left px-5 py-4">Payment</th>
                  <th className="text-left px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_id} className="border-b border-border last:border-b-0 hover:bg-secondary/50" data-testid={`booking-row-${b.booking_id}`}>
                    <td className="px-5 py-4">
                      <div className="font-semibold">{b.customer_name}</div>
                      <div className="text-muted-foreground text-xs">{b.customer_email}</div>
                      <div className="text-muted-foreground text-xs">{b.customer_phone}</div>
                    </td>
                    <td className="px-5 py-4">{b.tour_name}</td>
                    <td className="px-5 py-4">{b.date}<br /><span className="text-muted-foreground text-xs">{b.time_slot}</span></td>
                    <td className="px-5 py-4">{b.num_people}</td>
                    <td className="px-5 py-4 font-display font-semibold">${Number(b.amount).toFixed(2)}</td>
                    <td className="px-5 py-4">
                      <span className={`font-mono text-xs uppercase ${b.payment_status === "paid" ? "text-green-700" : "text-amber-700"}`}>
                        {b.payment_status}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <Select value={b.status} onValueChange={(v) => changeStatus(b.booking_id, v)}>
                        <SelectTrigger className={`rounded-none h-9 text-xs uppercase font-mono w-36 border ${STATUS_STYLES[b.status] || ""}`} data-testid={`status-select-${b.booking_id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </TabsContent>

          <TabsContent value="tours">
            <ToursManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
